import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, X, Users, LayoutGrid, Gamepad2, RefreshCw, Loader2, FileText, GripVertical, Star, Boxes, Heart, Mail, Zap, DollarSign, UserCog, Check } from "lucide-react";
import { api, formatNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ImageInput } from "@/components/ImageInput";

const emptyCreator = { roblox_input: "", department: "UGC", tier: "Member", discord_url: "", portfolio_url: "", featured: false, order: 0, name: "", avatar_url: "",
  socials: { twitter: "", instagram: "", youtube: "", tiktok: "", twitch: "", website: "" } };
const emptyDrop = { title: "", creator_id: "", image_url: "", link_url: "", active: true, order: 0 };
const DEPARTMENTS = [["UGC", "UGC Creators"], ["Builder", "Builders"], ["Scripter", "Scripters"], ["GFX", "GFX"], ["VFX", "VFX"]];
const emptyCreation = { title: "", description: "", media_type: "image", image_url: "", video_url: "", external_url: "", game: "", creator_id: "", size: "regular", order: 0 };

const inputCls = "w-full bg-background border border-border focus:border-volt outline-none px-3 py-2 text-sm transition-colors duration-200";
const labelCls = "font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground";

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("games");
  const [team, setTeam] = useState([]);
  const [creations, setCreations] = useState([]);
  const [games, setGames] = useState([]);
  const [groups, setGroups] = useState([]);
  const [drops, setDrops] = useState([]);
  const [orders, setOrders] = useState([]);
  const [staff, setStaff] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [site, setSite] = useState(null);
  const [editing, setEditing] = useState(null);

  const loadAll = () => {
    api.get("/team").then((r) => setTeam(r.data));
    api.get("/creations").then((r) => setCreations(r.data));
    api.get("/games").then((r) => setGames(r.data));
    api.get("/groups").then((r) => setGroups(r.data));
    api.get("/drops").then((r) => setDrops(r.data)).catch(() => {});
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
    api.get("/inquiries").then((r) => setInquiries(r.data)).catch(() => {});
    api.get("/site").then((r) => setSite(r.data));
  };
  useEffect(loadAll, []);

  const onLogout = async () => { await logout(); navigate("/"); };

  const saveCreator = async (data) => {
    try {
      if (data.id) await api.put(`/team/${data.id}`, data);
      else await api.post("/team", data);
      toast.success("Creator saved"); setEditing(null); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const saveCreation = async (data) => {
    try {
      if (data.id) await api.put(`/creations/${data.id}`, data);
      else await api.post("/creations", data);
      toast.success("Creation saved"); setEditing(null); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (type, id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/${type}/${id}`); toast.success("Deleted"); loadAll(); }
    catch (e) { toast.error("Delete failed"); }
  };
  const saveSite = async (data) => {
    try { await api.put("/site", data); toast.success("Content saved"); loadAll(); }
    catch (e) { toast.error("Save failed"); }
  };
  const saveDrop = async (data) => {
    try {
      if (data.id) await api.put(`/drops/${data.id}`, data);
      else await api.post("/drops", data);
      toast.success("Drop saved"); setEditing(null); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const dragRef = useRef(null);
  const handleDrop = async (collection, list, to) => {
    const from = dragRef.current; dragRef.current = null;
    if (from == null || from === to) return;
    const ids = list.map((x) => x.id);
    const [m] = ids.splice(from, 1); ids.splice(to, 0, m);
    await api.post("/reorder", { collection, ids });
    loadAll();
  };

  const tabs = [
    ["games", "Games", Gamepad2, games.length],
    ["communities", "Communities", Boxes, groups.length],
    ["drops", "Drops", Zap, drops.length],
    ["creators", "Creators", Users, team.length],
    ["creations", "Creations", LayoutGrid, creations.length],
    ["inquiries", "Inquiries", Mail, inquiries.length],
    ["orders", "Orders", DollarSign, orders.length],
    ["staff", "Staff", UserCog, staff.length],
    ["content", "Content", FileText, null],
  ];

  return (
    <div className="min-h-screen">
      <div className="border-b border-border sticky top-0 z-30 bg-black/70 backdrop-blur-2xl">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-display font-black tracking-tighter uppercase">Etoire<span className="text-volt">.</span> <span className="text-muted-foreground font-sans font-normal text-xs ml-2">Console</span></span>
          <div className="flex items-center gap-4">
            <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground hidden sm:block">{user?.username || user?.email}</span>
            <button data-testid="logout-btn" onClick={onLogout} className="inline-flex items-center gap-2 border border-border hover:border-volt hover:text-volt px-3 py-2 font-mono text-[10px] tracking-[0.2em] uppercase transition-colors duration-200">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="flex flex-wrap gap-px bg-border border border-border w-fit mb-10">
          {tabs.map(([key, label, Icon, count]) => (
            <button key={key} data-testid={`tab-${key}`} onClick={() => setTab(key)}
              className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-xs tracking-[0.2em] uppercase transition-colors duration-200 ${tab === key ? "bg-volt text-white" : "bg-card text-muted-foreground hover:text-foreground"}`}>
              <Icon className="w-4 h-4" /> {label} <span className="opacity-60">{count}</span>
            </button>
          ))}
        </div>

        {tab === "games" && <GamesPanel games={games} team={team} reload={loadAll} onDelete={(id) => del("games", id)} />}
        {tab === "communities" && <GroupsPanel groups={groups} reload={loadAll} onDelete={(id) => del("groups", id)} dragRef={dragRef} onReorderDrop={handleDrop} />}
        {tab === "drops" && <DropsPanel drops={drops} team={team} reload={loadAll} onEdit={(d) => setEditing({ type: "drop", data: { ...emptyDrop, ...d } })} onAdd={() => setEditing({ type: "drop", data: { ...emptyDrop } })} onDelete={(id) => del("drops", id)} dragRef={dragRef} onReorderDrop={handleDrop} />}

        {tab === "creators" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display font-bold text-2xl uppercase tracking-tight">Creators</h2>
              <button data-testid="add-creator-btn" onClick={() => setEditing({ type: "creator", data: { ...emptyCreator, socials: { ...emptyCreator.socials } } })} className="inline-flex items-center gap-2 bg-volt text-white px-4 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase hover:brightness-110 transition-all duration-200">
                <Plus className="w-4 h-4" /> New Creator
              </button>
            </div>
            <div className="border border-border divide-y divide-border">
              {team.length === 0 && <div className="p-8 text-muted-foreground font-mono text-sm">No creators yet. Add your team.</div>}
              {team.map((c, idx) => (
                <div key={c.id} draggable onDragStart={() => (dragRef.current = idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => handleDrop("team", team, idx)} data-testid={`admin-creator-row-${c.id}`} className="flex items-center gap-3 p-4 hover:bg-card transition-colors duration-200 cursor-grab active:cursor-grabbing">
                  <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
                  <img src={c.avatar_url || "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=200"} alt="" className="w-12 h-12 object-cover grayscale" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold uppercase tracking-tight truncate">{c.name}</div>
                    <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground">{c.tier === "Lead" ? "Lead · " : ""}{(DEPARTMENTS.find((d) => d[0] === (c.department || "UGC")) || [])[1]} · @{c.roblox_username || "roblox"}</div>
                  </div>
                  <button data-testid={`edit-creator-${c.id}`} onClick={() => setEditing({ type: "creator", data: { id: c.id, roblox_input: c.roblox_url || c.roblox_username || "", department: c.department || "UGC", tier: c.tier || "Member", discord_url: c.discord_url || "", portfolio_url: c.portfolio_url || "", featured: c.featured, order: c.order, name: c.name, avatar_url: c.avatar_url, socials: { ...emptyCreator.socials, ...c.socials } } })} className="p-2 border border-border hover:border-volt hover:text-volt transition-colors duration-200"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`delete-creator-${c.id}`} onClick={() => del("team", c.id)} className="p-2 border border-border hover:border-destructive hover:text-destructive transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "creations" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display font-bold text-2xl uppercase tracking-tight">Creations</h2>
              <button data-testid="add-creation-btn" onClick={() => setEditing({ type: "creation", data: { ...emptyCreation } })} className="inline-flex items-center gap-2 bg-volt text-white px-4 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase hover:brightness-110 transition-all duration-200">
                <Plus className="w-4 h-4" /> New Creation
              </button>
            </div>
            <div className="border border-border divide-y divide-border">
              {creations.length === 0 && <div className="p-8 text-muted-foreground font-mono text-sm">No creations yet.</div>}
              {creations.map((c, idx) => (
                <div key={c.id} draggable onDragStart={() => (dragRef.current = idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => handleDrop("creations", creations, idx)} data-testid={`admin-creation-row-${c.id}`} className="flex items-center gap-3 p-4 hover:bg-card transition-colors duration-200 cursor-grab active:cursor-grabbing">
                  <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
                  <img src={c.image_url || "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=200"} alt="" className="w-12 h-12 object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold uppercase tracking-tight truncate">{c.title}</div>
                    <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground">{c.media_type} · {c.game}</div>
                  </div>
                  <div className="hidden md:block font-mono text-[10px] text-muted-foreground truncate max-w-[140px]">{team.find((t) => t.id === c.creator_id)?.name || "—"}</div>
                  <button data-testid={`edit-creation-${c.id}`} onClick={() => setEditing({ type: "creation", data: { ...emptyCreation, ...c } })} className="p-2 border border-border hover:border-volt hover:text-volt transition-colors duration-200"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`delete-creation-${c.id}`} onClick={() => del("creations", c.id)} className="p-2 border border-border hover:border-destructive hover:text-destructive transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === "content" && site && <ContentPanel site={site} onSave={saveSite} />}
        {tab === "inquiries" && <InquiriesPanel inquiries={inquiries} onDelete={(id) => del("inquiries", id)} />}
        {tab === "orders" && <OrdersPanel orders={orders} reload={loadAll} />}
        {tab === "staff" && <StaffPanel staff={staff} reload={loadAll} role={user?.role} />}
      </div>

      {editing?.type === "creator" && <CreatorForm data={editing.data} onClose={() => setEditing(null)} onSave={saveCreator} />}
      {editing?.type === "creation" && <CreationForm data={editing.data} team={team} onClose={() => setEditing(null)} onSave={saveCreation} />}
      {editing?.type === "drop" && <DropForm data={editing.data} team={team} onClose={() => setEditing(null)} onSave={saveDrop} />}
    </div>
  );
}

function GamesPanel({ games, team, reload, onDelete }) {
  const [input, setInput] = useState("");
  const [creatorId, setCreatorId] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const dragRef = useRef(null);
  const onReorder = async (to) => {
    const from = dragRef.current; dragRef.current = null;
    if (from == null || from === to) return;
    const ids = games.map((x) => x.id);
    const [m] = ids.splice(from, 1); ids.splice(to, 0, m);
    await api.post("/reorder", { collection: "games", ids });
    reload();
  };

  const connect = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setConnecting(true);
    try {
      await api.post("/games", { input: input.trim(), creator_id: creatorId });
      toast.success("Game connected");
      setInput(""); setCreatorId(""); reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not connect game");
    } finally { setConnecting(false); }
  };

  const refresh = async () => {
    setRefreshing(true);
    try { await api.get("/games/refresh"); toast.success("Stats refreshed"); reload(); }
    catch (e) { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  };

  const assignCreator = async (id, creator_id) => {
    try { await api.put(`/games/${id}`, { creator_id }); reload(); }
    catch (e) { toast.error("Update failed"); }
  };
  const toggleFeature = async (id, current) => {
    try { await api.put(`/games/${id}`, { featured: !current }); toast.success(!current ? "Featured" : "Unfeatured"); reload(); }
    catch (e) { toast.error("Update failed"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display font-bold text-2xl uppercase tracking-tight">Roblox Games</h2>
        <button data-testid="refresh-games-btn" onClick={refresh} disabled={refreshing} className="inline-flex items-center gap-2 border border-border hover:border-volt hover:text-volt px-4 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase transition-colors duration-200 disabled:opacity-50">
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh Stats
        </button>
      </div>

      <form data-testid="connect-game-form" onSubmit={connect} className="border border-border bg-card p-5 mb-8 grid md:grid-cols-[1fr_220px_auto] gap-3 items-end">
        <div className="space-y-1.5">
          <label className={labelCls}>Roblox Game URL or Place ID</label>
          <input data-testid="game-input" className={inputCls} placeholder="https://www.roblox.com/games/1818/Classic-Crossroads" value={input} onChange={(e) => setInput(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <label className={labelCls}>Assign Creator (optional)</label>
          <select data-testid="game-creator" className={inputCls} value={creatorId} onChange={(e) => setCreatorId(e.target.value)}>
            <option value="">— Unassigned —</option>
            {team.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <button data-testid="connect-game-btn" type="submit" disabled={connecting} className="inline-flex items-center justify-center gap-2 bg-volt text-white px-5 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase hover:brightness-110 transition-all duration-200 disabled:opacity-50 h-[38px]">
          {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Connect
        </button>
      </form>

      <div className="border border-border divide-y divide-border">
        {games.length === 0 && <div className="p-8 text-muted-foreground font-mono text-sm">No games connected. Paste a Roblox game URL above to start tracking live stats.</div>}
        {games.map((g, idx) => (
          <div key={g.id} draggable onDragStart={() => (dragRef.current = idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => onReorder(idx)} data-testid={`admin-game-row-${g.id}`} className="flex items-center gap-3 p-4 hover:bg-card transition-colors duration-200 cursor-grab active:cursor-grabbing">
            <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
            <img src={g.thumbnail_url || "https://images.unsplash.com/photo-1552820728-8b83bb6b773f?w=200"} alt="" className="w-14 h-14 object-cover" />
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold uppercase tracking-tight truncate">{g.name || "Untitled"}</div>
              <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground">Universe {g.universe_id} · {team.find((t) => t.id === g.creator_id)?.name || "Unassigned"}</div>
            </div>
            <div className="hidden lg:flex gap-6 font-mono text-[11px] text-muted-foreground">
              <span><span className="text-volt">{formatNum(g.playing)}</span> playing</span>
              <span><span className="text-foreground">{formatNum(g.visits)}</span> visits</span>
              <span><span className="text-foreground">{g.like_ratio || 0}%</span> rating</span>
            </div>
            <select data-testid={`game-assign-${g.id}`} value={g.creator_id || ""} onChange={(e) => assignCreator(g.id, e.target.value)} onClick={(e) => e.stopPropagation()}
              className="hidden sm:block bg-background border border-border focus:border-volt outline-none px-2 py-1.5 text-[11px] font-mono max-w-[130px] transition-colors duration-200">
              <option value="">Unassigned</option>
              {team.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button data-testid={`feature-game-${g.id}`} title="Feature on homepage" onClick={() => toggleFeature(g.id, g.featured)}
              className={`p-2 border transition-colors duration-200 ${g.featured ? "border-volt text-volt bg-volt/10" : "border-border text-muted-foreground hover:border-volt hover:text-volt"}`}>
              <Star className="w-4 h-4" fill={g.featured ? "currentColor" : "none"} />
            </button>
            <button data-testid={`delete-game-${g.id}`} onClick={() => onDelete(g.id)} className="p-2 border border-border hover:border-destructive hover:text-destructive transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Drawer({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex justify-end" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-lenis-prevent className="w-full max-w-lg bg-card border-l border-border h-full overflow-y-auto overscroll-contain">
        <div className="sticky top-0 z-10 bg-card border-b border-border px-6 h-16 flex items-center justify-between">
          <h3 className="font-display font-bold uppercase tracking-tight">{title}</h3>
          <button data-testid="form-close" onClick={onClose} className="p-2 hover:text-volt transition-colors duration-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><label className={labelCls}>{label}</label>{children}</div>;
}

function CreatorForm({ data, onClose, onSave }) {
  const [form, setForm] = useState(data);
  const [preview, setPreview] = useState(data.name ? { name: data.name, avatar_url: data.avatar_url } : null);
  const [fetching, setFetching] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSocial = (k, v) => setForm((f) => ({ ...f, socials: { ...f.socials, [k]: v } }));
  const setRoblox = (v) => { setForm((f) => ({ ...f, roblox_input: v })); setPreview(null); };

  const fetchProfile = async () => {
    if (!form.roblox_input.trim()) { toast.error("Paste a Roblox profile URL or username"); return; }
    setFetching(true);
    try {
      const r = await api.post("/team/resolve", { input: form.roblox_input.trim() });
      setPreview({ name: r.data.name, avatar_url: r.data.avatar_url });
      toast.success(`Found ${r.data.name}`);
    } catch (e) {
      setPreview(null);
      toast.error(e.response?.data?.detail || "Could not find that Roblox user");
    } finally { setFetching(false); }
  };

  return (
    <Drawer title={data.id ? "Edit Creator" : "New Creator"} onClose={onClose}>
      <form data-testid="creator-form" onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-5">
        <Field label="Roblox Profile URL or Username">
          <div className="flex gap-2">
            <input data-testid="creator-roblox-input" className={inputCls} value={form.roblox_input} onChange={(e) => setRoblox(e.target.value)} placeholder="https://www.roblox.com/users/123/profile or username" required />
            <button type="button" data-testid="creator-fetch-btn" onClick={fetchProfile} disabled={fetching}
              className="shrink-0 inline-flex items-center gap-2 border border-border hover:border-volt hover:text-volt px-4 font-mono text-[10px] tracking-[0.2em] uppercase transition-colors duration-200 disabled:opacity-50">
              {fetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Fetch
            </button>
          </div>
        </Field>

        {preview && (
          <div data-testid="creator-preview" className="flex items-center gap-3 border border-border bg-background p-3">
            <img src={preview.avatar_url || "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=200"} alt="" className="w-14 h-14 object-cover" />
            <div>
              <div className="font-display font-bold uppercase tracking-tight">{preview.name}</div>
              <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground">Name & avatar pulled from Roblox</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Department">
            <select data-testid="creator-department" className={inputCls} value={form.department} onChange={(e) => set("department", e.target.value)}>
              {DEPARTMENTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
          <Field label="Tier">
            <select data-testid="creator-tier" className={inputCls} value={form.tier} onChange={(e) => set("tier", e.target.value)}>
              <option value="Lead">Lead</option>
              <option value="Member">Member</option>
            </select>
          </Field>
        </div>

        <Field label="Discord Server Invite">
          <input data-testid="creator-discord" className={inputCls} value={form.discord_url} onChange={(e) => set("discord_url", e.target.value)} placeholder="https://discord.gg/…" />
        </Field>

        <Field label="Portfolio Link">
          <input data-testid="creator-portfolio" className={inputCls} value={form.portfolio_url} onChange={(e) => set("portfolio_url", e.target.value)} placeholder="https://… (optional)" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          {["twitter", "instagram", "youtube", "tiktok", "twitch", "website"].map((s) => (
            <Field key={s} label={s}><input className={inputCls} value={form.socials[s]} onChange={(e) => setSocial(s, e.target.value)} /></Field>
          ))}
        </div>
        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} /> <span className={labelCls}>Featured</span></label>
        <button data-testid="creator-save" type="submit" className="w-full bg-volt text-white font-mono text-xs tracking-[0.2em] uppercase py-3.5 hover:brightness-110 transition-all duration-200">Save Creator</button>
      </form>
    </Drawer>
  );
}

function CreationForm({ data, team, onClose, onSave }) {
  const [form, setForm] = useState(data);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Drawer title={data.id ? "Edit Creation" : "New Creation"} onClose={onClose}>
      <form data-testid="creation-form" onSubmit={(e) => {
        e.preventDefault();
        if (form.media_type === "video" && !form.video_url) { toast.error("Upload a video or paste a video URL"); return; }
        if (form.media_type === "link" && !form.external_url) { toast.error("Add the external project URL"); return; }
        onSave(form);
      }} className="space-y-5">
        <Field label="Title"><input data-testid="creation-title" className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} required /></Field>
        <Field label="Description"><textarea className={inputCls} rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Game / Category"><input className={inputCls} value={form.game} onChange={(e) => set("game", e.target.value)} /></Field>
          <Field label="Media Type">
            <select data-testid="creation-media-type" className={inputCls} value={form.media_type} onChange={(e) => set("media_type", e.target.value)}>
              <option value="image">Image</option>
              <option value="video">Video Embed</option>
              <option value="link">External Link</option>
            </select>
          </Field>
        </div>
        <Field label="Creator">
          <select data-testid="creation-creator" className={inputCls} value={form.creator_id} onChange={(e) => set("creator_id", e.target.value)}>
            <option value="">— Unassigned —</option>
            {team.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>
        <Field label="Image / Thumbnail"><ImageInput value={form.image_url} onChange={(v) => set("image_url", v)} testid="creation-image" /></Field>
        {form.media_type === "video" && <Field label="Video — upload a file or paste a YouTube/TikTok embed URL"><ImageInput kind="video" value={form.video_url} onChange={(v) => set("video_url", v)} testid="creation-video" placeholder="Upload a video or paste an embed URL" /></Field>}
        {form.media_type === "link" && <Field label="External Project URL"><input className={inputCls} value={form.external_url} onChange={(e) => set("external_url", e.target.value)} /></Field>}
        <Field label="Bento Size">
          <select className={inputCls} value={form.size} onChange={(e) => set("size", e.target.value)}>
            <option value="regular">Regular</option>
            <option value="wide">Wide</option>
            <option value="tall">Tall</option>
            <option value="large">Large</option>
          </select>
        </Field>
        <button data-testid="creation-save" type="submit" className="w-full bg-volt text-white font-mono text-xs tracking-[0.2em] uppercase py-3.5 hover:brightness-110 transition-all duration-200">Save Creation</button>
      </form>
    </Drawer>
  );
}

function Sect({ title, children }) {
  return (
    <div className="space-y-4">
      <h3 className="font-mono text-[10px] tracking-[0.25em] uppercase text-volt border-b border-border pb-2">{title}</h3>
      {children}
    </div>
  );
}

function ContentPanel({ site, onSave }) {
  const norm = {
    ...site,
    hero_lines: [0, 1, 2].map((i) => (site.hero_lines || [])[i] || ""),
    manifesto: [0, 1, 2].map((i) => (site.manifesto || [])[i] || { n: "", title: "", body: "" }),
  };
  const [f, setF] = useState(norm);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setLine = (i, v) => setF((p) => { const a = [...p.hero_lines]; a[i] = v; return { ...p, hero_lines: a }; });
  const setMani = (i, k, v) => setF((p) => { const a = p.manifesto.map((m) => ({ ...m })); a[i][k] = v; return { ...p, manifesto: a }; });
  const marqueeStr = Array.isArray(f.marquee_items) ? f.marquee_items.join(", ") : (f.marquee_items || "");
  const emailsStr = Array.isArray(f.campaign_emails) ? f.campaign_emails.join(", ") : (f.campaign_emails || "");
  const notifyStr = Array.isArray(f.notify_emails) ? f.notify_emails.join(", ") : (f.notify_emails || "");

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...f,
      hero_lines: f.hero_lines.filter((l) => l !== undefined),
      marquee_items: typeof f.marquee_items === "string"
        ? f.marquee_items.split(",").map((s) => s.trim()).filter(Boolean)
        : f.marquee_items,
      campaign_emails: typeof f.campaign_emails === "string"
        ? f.campaign_emails.split(",").map((s) => s.trim()).filter(Boolean)
        : f.campaign_emails,
      notify_emails: typeof f.notify_emails === "string"
        ? f.notify_emails.split(",").map((s) => s.trim()).filter(Boolean)
        : f.notify_emails,
    });
  };

  return (
    <form data-testid="content-form" onSubmit={submit} className="max-w-2xl space-y-10">
      <Sect title="Hero">
        <Field label="Eyebrow"><input data-testid="content-hero-eyebrow" className={inputCls} value={f.hero_eyebrow || ""} onChange={(e) => set("hero_eyebrow", e.target.value)} /></Field>
        <Field label="Background Image"><ImageInput value={f.hero_image_url} onChange={(v) => set("hero_image_url", v)} testid="content-hero-image" /></Field>
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Field key={i} label={`Headline line ${i + 1}`}><input className={inputCls} value={f.hero_lines[i] || ""} onChange={(e) => setLine(i, e.target.value)} /></Field>
          ))}
        </div>
        <Field label="Paragraph"><textarea className={inputCls} rows={3} value={f.hero_paragraph || ""} onChange={(e) => set("hero_paragraph", e.target.value)} /></Field>
        <Field label="Button Label"><input className={inputCls} value={f.hero_cta_label || ""} onChange={(e) => set("hero_cta_label", e.target.value)} /></Field>
      </Sect>

      <Sect title="Marquee">
        <Field label="Words (comma separated)"><input data-testid="content-marquee" className={inputCls} value={marqueeStr} onChange={(e) => set("marquee_items", e.target.value)} /></Field>
      </Sect>

      <Sect title="Revenue Counter Baseline">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Starting gross ($)"><input data-testid="content-revenue-gross" type="number" step="0.01" className={inputCls} value={f.revenue_base_gross ?? 0} onChange={(e) => set("revenue_base_gross", e.target.value === "" ? 0 : parseFloat(e.target.value))} /></Field>
          <Field label="Starting orders fulfilled"><input data-testid="content-revenue-orders" type="number" className={inputCls} value={f.revenue_base_orders ?? 0} onChange={(e) => set("revenue_base_orders", e.target.value === "" ? 0 : parseInt(e.target.value, 10))} /></Field>
        </div>
        <Field label="Monthly revenue goal ($)"><input data-testid="content-revenue-goal" type="number" step="1" className={inputCls} value={f.revenue_goal ?? 0} onChange={(e) => set("revenue_goal", e.target.value === "" ? 0 : parseFloat(e.target.value))} /></Field>
        <Field label="Order notification emails (comma separated)"><input data-testid="content-notify-emails" className={inputCls} value={notifyStr} onChange={(e) => set("notify_emails", e.target.value)} /></Field>
        <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground">New paid orders add on top of these starting numbers on the live counter. Notifications are emailed to the addresses above.</p>
      </Sect>

      <Sect title="Studio Section">
        <Field label="Eyebrow"><input className={inputCls} value={f.studio_eyebrow || ""} onChange={(e) => set("studio_eyebrow", e.target.value)} /></Field>
        <Field label="Heading"><input className={inputCls} value={f.studio_heading || ""} onChange={(e) => set("studio_heading", e.target.value)} /></Field>
        <Field label="Body"><textarea className={inputCls} rows={3} value={f.studio_body || ""} onChange={(e) => set("studio_body", e.target.value)} /></Field>
      </Sect>

      <Sect title="Games Section">
        <Field label="Eyebrow"><input className={inputCls} value={f.games_eyebrow || ""} onChange={(e) => set("games_eyebrow", e.target.value)} /></Field>
        <Field label="Heading"><input className={inputCls} value={f.games_heading || ""} onChange={(e) => set("games_heading", e.target.value)} /></Field>
      </Sect>

      <Sect title="UGC Drops Section">
        <Field label="Eyebrow"><input data-testid="content-drops-eyebrow" className={inputCls} value={f.drops_eyebrow || ""} onChange={(e) => set("drops_eyebrow", e.target.value)} /></Field>
        <Field label="Heading"><input className={inputCls} value={f.drops_heading || ""} onChange={(e) => set("drops_heading", e.target.value)} /></Field>
        <Field label="Body"><textarea className={inputCls} rows={2} value={f.drops_body || ""} onChange={(e) => set("drops_body", e.target.value)} /></Field>
      </Sect>

      <Sect title="Creators Section">
        <Field label="Eyebrow"><input className={inputCls} value={f.creators_eyebrow || ""} onChange={(e) => set("creators_eyebrow", e.target.value)} /></Field>
        <Field label="Heading"><input className={inputCls} value={f.creators_heading || ""} onChange={(e) => set("creators_heading", e.target.value)} /></Field>
      </Sect>

      <Sect title="Work Section">
        <Field label="Eyebrow"><input className={inputCls} value={f.work_eyebrow || ""} onChange={(e) => set("work_eyebrow", e.target.value)} /></Field>
        <Field label="Heading"><input className={inputCls} value={f.work_heading || ""} onChange={(e) => set("work_heading", e.target.value)} /></Field>
        <Field label="Body"><textarea className={inputCls} rows={2} value={f.work_body || ""} onChange={(e) => set("work_body", e.target.value)} /></Field>
      </Sect>

      <Sect title="Campaign / Contact Section">
        <Field label="Eyebrow"><input data-testid="content-campaign-eyebrow" className={inputCls} value={f.campaign_eyebrow || ""} onChange={(e) => set("campaign_eyebrow", e.target.value)} /></Field>
        <Field label="Heading"><input className={inputCls} value={f.campaign_heading || ""} onChange={(e) => set("campaign_heading", e.target.value)} /></Field>
        <Field label="Body"><textarea className={inputCls} rows={3} value={f.campaign_body || ""} onChange={(e) => set("campaign_body", e.target.value)} /></Field>
        <Field label="Business emails (comma separated)"><input data-testid="content-campaign-emails" className={inputCls} value={emailsStr} onChange={(e) => set("campaign_emails", e.target.value)} /></Field>
      </Sect>

      <Sect title="Footer">
        <Field label="Tagline"><textarea className={inputCls} rows={2} value={f.footer_tagline || ""} onChange={(e) => set("footer_tagline", e.target.value)} /></Field>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Twitter URL"><input className={inputCls} value={f.footer_twitter || ""} onChange={(e) => set("footer_twitter", e.target.value)} /></Field>
          <Field label="YouTube URL"><input className={inputCls} value={f.footer_youtube || ""} onChange={(e) => set("footer_youtube", e.target.value)} /></Field>
          <Field label="Discord URL"><input data-testid="content-footer-discord" className={inputCls} value={f.footer_discord || ""} onChange={(e) => set("footer_discord", e.target.value)} /></Field>
        </div>
        <Field label="Copyright"><input className={inputCls} value={f.footer_copyright || ""} onChange={(e) => set("footer_copyright", e.target.value)} /></Field>
        <Field label="Credit line"><input className={inputCls} value={f.footer_credit || ""} onChange={(e) => set("footer_credit", e.target.value)} /></Field>
      </Sect>

      <Sect title="Manifesto">
        {[0, 1, 2].map((i) => (
          <div key={i} className="border border-border p-4 space-y-3">
            <div className="grid grid-cols-4 gap-3">
              <Field label="No."><input className={inputCls} value={f.manifesto[i]?.n || ""} onChange={(e) => setMani(i, "n", e.target.value)} /></Field>
              <div className="col-span-3"><Field label="Title"><input className={inputCls} value={f.manifesto[i]?.title || ""} onChange={(e) => setMani(i, "title", e.target.value)} /></Field></div>
            </div>
            <Field label="Body"><textarea className={inputCls} rows={2} value={f.manifesto[i]?.body || ""} onChange={(e) => setMani(i, "body", e.target.value)} /></Field>
          </div>
        ))}
      </Sect>

      <button data-testid="content-save" type="submit" className="w-full bg-volt text-white font-mono text-xs tracking-[0.2em] uppercase py-3.5 hover:brightness-110 transition-all duration-200">Save Content</button>
    </form>
  );
}

function GroupsPanel({ groups, reload, onDelete, dragRef, onReorderDrop }) {
  const [input, setInput] = useState("");
  const [connecting, setConnecting] = useState(false);

  const connect = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setConnecting(true);
    try {
      await api.post("/groups", { input: input.trim() });
      toast.success("Community connected — pulling UGC…");
      setInput(""); reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not connect community");
    } finally { setConnecting(false); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <h2 className="font-display font-bold text-2xl uppercase tracking-tight">Communities</h2>
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Favorites auto-refresh every 30s</span>
      </div>

      <form data-testid="group-connect-form" onSubmit={connect} className="flex flex-col sm:flex-row gap-3 mb-8">
        <input data-testid="group-input" className="flex-1 bg-background border border-border focus:border-volt outline-none px-4 py-3 text-sm transition-colors duration-200"
          value={input} onChange={(e) => setInput(e.target.value)} placeholder="Roblox group/community URL or ID (e.g. https://www.roblox.com/communities/12345/…)" />
        <button data-testid="connect-group-btn" type="submit" disabled={connecting}
          className="inline-flex items-center justify-center gap-2 bg-volt text-white px-6 py-3 font-mono text-[10px] tracking-[0.2em] uppercase hover:brightness-110 disabled:opacity-50 transition-all duration-200">
          {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {connecting ? "Adding…" : "Add Community"}
        </button>
      </form>

      <div className="border border-border divide-y divide-border">
        {groups.length === 0 && <div className="p-8 text-center text-muted-foreground font-mono text-xs">No communities yet. Add a Roblox group that drops UGC above.</div>}
        {groups.map((g, idx) => (
          <div key={g.id} draggable onDragStart={() => (dragRef.current = idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => onReorderDrop("groups", groups, idx)}
            data-testid={`admin-group-row-${g.id}`} className="flex items-center gap-3 p-4 hover:bg-card transition-colors duration-200 cursor-grab active:cursor-grabbing">
            <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
            <img src={g.thumbnail_url || "https://images.unsplash.com/photo-1552820728-8b83bb6b773f?w=200"} alt="" className="w-12 h-12 object-cover" />
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold uppercase tracking-tight truncate">{g.name || "Community"}</div>
              <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground">Group {g.group_id} · {g.ugc_count} UGC · {g.clothing_count || 0} Clothing</div>
            </div>
            <div className="hidden sm:flex items-center gap-1.5 font-mono text-[11px]">
              <Heart className="w-3.5 h-3.5 text-volt" fill="currentColor" /> <span className="text-volt">{formatNum(g.total_favorites)}</span>
            </div>
            <button data-testid={`delete-group-${g.id}`} onClick={() => onDelete(g.id)} className="p-2 border border-border hover:border-destructive hover:text-destructive transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}


function InquiriesPanel({ inquiries, onDelete }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display font-bold text-2xl uppercase tracking-tight">Inquiries</h2>
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{inquiries.length} total</span>
      </div>
      {inquiries.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center text-muted-foreground font-mono text-xs">No inquiries yet.</div>
      ) : (
        <div className="space-y-4">
          {inquiries.map((q) => (
            <div key={q.id} data-testid={`inquiry-${q.id}`} className="border border-border bg-card p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-volt border border-volt/40 px-2 py-1">{q.inquiry_type}</span>
                  <div className="font-display font-bold text-lg uppercase tracking-tight mt-3">{q.full_name}</div>
                  <div className="font-mono text-[11px] text-muted-foreground mt-1 truncate">
                    <a href={`mailto:${q.email}`} className="hover:text-volt transition-colors duration-200">{q.email}</a>{q.company ? ` · ${q.company}` : ""}
                  </div>
                </div>
                <button onClick={() => onDelete(q.id)} data-testid={`delete-inquiry-${q.id}`} className="shrink-0 p-2 border border-border hover:border-destructive hover:text-destructive transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
              </div>
              <p className="text-sm text-foreground/90 mt-4 whitespace-pre-wrap">{q.message}</p>
              <div className="font-mono text-[9px] tracking-[0.15em] uppercase text-muted-foreground mt-4">{new Date(q.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function DropsPanel({ drops, team, reload, onEdit, onAdd, onDelete, dragRef, onReorderDrop }) {
  const toggleActive = async (d) => {
    try { await api.put(`/drops/${d.id}`, { ...d, active: !d.active }); reload(); }
    catch (e) { toast.error("Update failed"); }
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-2 flex-wrap gap-3">
        <h2 className="font-display font-bold text-2xl uppercase tracking-tight">Fresh Drops</h2>
        <button data-testid="add-drop-btn" onClick={onAdd} className="inline-flex items-center gap-2 bg-volt text-white px-4 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase hover:brightness-110 transition-all duration-200">
          <Plus className="w-4 h-4" /> New Drop
        </button>
      </div>
      <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground mb-6">Active drops play as a cinematic intro slideshow on the homepage every refresh, then fade away.</p>

      <div className="border border-border divide-y divide-border">
        {drops.length === 0 && <div className="p-8 text-muted-foreground font-mono text-sm">No drops yet. Add a newly-dropped item to feature it in the homepage intro.</div>}
        {drops.map((d, idx) => (
          <div key={d.id} draggable onDragStart={() => (dragRef.current = idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => onReorderDrop("drops", drops, idx)}
            data-testid={`admin-drop-row-${d.id}`} className={`flex items-center gap-3 p-4 hover:bg-card transition-colors duration-200 cursor-grab active:cursor-grabbing ${d.active ? "" : "opacity-50"}`}>
            <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
            <img src={d.image_url || "https://images.unsplash.com/photo-1552820728-8b83bb6b773f?w=200"} alt="" className="w-14 h-14 object-cover" />
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold uppercase tracking-tight truncate">{d.title}</div>
              <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground truncate">{team.find((t) => t.id === d.creator_id)?.name || "Unassigned"}{d.link_url ? " · linked" : ""}</div>
            </div>
            <button data-testid={`toggle-drop-${d.id}`} onClick={() => toggleActive(d)} title={d.active ? "Active" : "Hidden"}
              className={`p-2 border transition-colors duration-200 ${d.active ? "border-volt text-volt bg-volt/10" : "border-border text-muted-foreground hover:border-volt hover:text-volt"}`}>
              <Zap className="w-4 h-4" fill={d.active ? "currentColor" : "none"} />
            </button>
            <button data-testid={`edit-drop-${d.id}`} onClick={() => onEdit(d)} className="p-2 border border-border hover:border-volt hover:text-volt transition-colors duration-200"><Pencil className="w-4 h-4" /></button>
            <button data-testid={`delete-drop-${d.id}`} onClick={() => onDelete(d.id)} className="p-2 border border-border hover:border-destructive hover:text-destructive transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DropForm({ data, team, onClose, onSave }) {
  const [form, setForm] = useState(data);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Drawer title={data.id ? "Edit Drop" : "New Drop"} onClose={onClose}>
      <form data-testid="drop-form" onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-5">
        <Field label="Title"><input data-testid="drop-title" className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} required /></Field>
        <Field label="Image"><ImageInput value={form.image_url} onChange={(v) => set("image_url", v)} testid="drop-image" /></Field>
        <Field label="Creator">
          <select data-testid="drop-creator" className={inputCls} value={form.creator_id} onChange={(e) => set("creator_id", e.target.value)}>
            <option value="">— Unassigned —</option>
            {team.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>
        <Field label="Link (Roblox catalog / upload URL)"><input data-testid="drop-link" className={inputCls} value={form.link_url} onChange={(e) => set("link_url", e.target.value)} placeholder="https://www.roblox.com/catalog/… (optional)" /></Field>
        <label className="flex items-center gap-2 cursor-pointer"><input data-testid="drop-active" type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} /> <span className={labelCls}>Active (show in homepage intro)</span></label>
        <button data-testid="drop-save" type="submit" className="w-full bg-volt text-white font-mono text-xs tracking-[0.2em] uppercase py-3.5 hover:brightness-110 transition-all duration-200">Save Drop</button>
      </form>
    </Drawer>
  );
}


const METHOD_LABEL = { stripe: "Card", cashapp: "CashApp", applepay: "Apple Pay", square: "Square" };

function OrdersPanel({ orders, reload }) {
  const paid = orders.filter((o) => o.status === "paid");
  const gross = paid.reduce((s, o) => s + Number(o.amount || 0), 0);
  const pending = orders.filter((o) => o.status === "pending" && o.method !== "stripe");

  const confirm = async (o) => { try { await api.post(`/orders/${o.id}/confirm`); toast.success("Marked as paid"); reload(); } catch { toast.error("Failed"); } };
  const unconfirm = async (o) => { try { await api.post(`/orders/${o.id}/unconfirm`); toast.success("Reverted to pending"); reload(); } catch { toast.error("Failed"); } };
  const remove = async (o) => { try { await api.delete(`/orders/${o.id}`); toast.success("Deleted"); reload(); } catch { toast.error("Failed"); } };

  const statusCls = (s) => s === "paid" ? "text-volt border-volt/50 bg-volt/10" : s === "pending" ? "text-muted-foreground border-border" : "text-destructive border-destructive/50";

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        <div className="border border-border bg-card p-5"><div className="font-display font-black text-3xl tracking-tighter text-volt">${gross.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div><span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Gross revenue</span></div>
        <div className="border border-border bg-card p-5"><div className="font-display font-black text-3xl tracking-tighter">{paid.length}</div><span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Paid orders</span></div>
        <div className="border border-border bg-card p-5"><div className="font-display font-black text-3xl tracking-tighter">{pending.length}</div><span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Awaiting confirm</span></div>
      </div>

      <h2 className="font-display font-bold text-2xl uppercase tracking-tight mb-5">Orders</h2>
      {orders.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center text-muted-foreground font-mono text-xs">No orders yet.</div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-row-${o.id}`} className="border border-border bg-card p-4 flex flex-wrap items-center gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold uppercase tracking-tight">{o.label}</span>
                  <span className="font-display font-black tracking-tighter text-volt">${o.amount}</span>
                  <span className={`font-mono text-[9px] tracking-[0.15em] uppercase border px-2 py-0.5 ${statusCls(o.status)}`} data-testid={`order-status-${o.id}`}>{o.status}</span>
                  <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-muted-foreground border border-border px-2 py-0.5">{METHOD_LABEL[o.method] || o.method}</span>
                </div>
                <div className="font-mono text-[11px] text-muted-foreground mt-1 truncate">{o.customer_name || "—"} · {o.customer_contact || "no contact"}</div>
                {o.notes && <p className="text-xs text-foreground/80 mt-1 line-clamp-2">{o.notes}</p>}
                <div className="font-mono text-[9px] tracking-[0.1em] uppercase text-muted-foreground mt-1">{new Date(o.created_at).toLocaleString()}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                {o.status !== "paid" && o.method !== "stripe" && (
                  <button data-testid={`confirm-order-${o.id}`} onClick={() => confirm(o)} className="inline-flex items-center gap-1.5 bg-volt text-white px-3 py-2 font-mono text-[10px] tracking-[0.15em] uppercase hover:brightness-110 transition-all duration-200"><Check className="w-3.5 h-3.5" /> Mark paid</button>
                )}
                {o.status === "paid" && o.method !== "stripe" && (
                  <button onClick={() => unconfirm(o)} className="px-3 py-2 border border-border font-mono text-[10px] tracking-[0.15em] uppercase hover:border-volt hover:text-volt transition-colors duration-200">Undo</button>
                )}
                <button data-testid={`delete-order-${o.id}`} onClick={() => remove(o)} className="p-2 border border-border hover:border-destructive hover:text-destructive transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StaffPanel({ staff, reload, role }) {
  const [form, setForm] = useState({ username: "", name: "", password: "" });
  const [busy, setBusy] = useState(false);
  const isOwner = role === "admin" || role === "owner";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/staff", form); toast.success("Staff account created"); setForm({ username: "", name: "", password: "" }); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not create staff"); }
    finally { setBusy(false); }
  };
  const remove = async (s) => { try { await api.delete(`/staff/${s.id}`); toast.success("Removed"); reload(); } catch { toast.error("Failed"); } };

  if (!isOwner) return <div className="border border-dashed border-border p-12 text-center text-muted-foreground font-mono text-xs">Only the owner can manage staff accounts.</div>;

  return (
    <div className="max-w-2xl">
      <h2 className="font-display font-bold text-2xl uppercase tracking-tight mb-2">Staff Logins</h2>
      <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground mb-6">Create individual logins for your team. Staff sign in at /login with their username &amp; password and can manage the site.</p>

      <form data-testid="staff-form" onSubmit={add} className="border border-border bg-card p-5 space-y-4 mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Username"><input data-testid="staff-username" className={inputCls} value={form.username} onChange={(e) => set("username", e.target.value)} placeholder="e.g. jasper" required /></Field>
          <Field label="Display name"><input data-testid="staff-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="optional" /></Field>
        </div>
        <Field label="Password (6+ characters)"><input data-testid="staff-password" type="text" className={inputCls} value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="set a password" required /></Field>
        <button data-testid="staff-create" type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-volt text-white px-5 py-3 font-mono text-[10px] tracking-[0.2em] uppercase hover:brightness-110 transition-all duration-200 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create staff</button>
      </form>

      <div className="border border-border divide-y divide-border">
        {staff.length === 0 && <div className="p-8 text-muted-foreground font-mono text-sm">No staff accounts yet.</div>}
        {staff.map((s) => (
          <div key={s.id} data-testid={`staff-row-${s.id}`} className="flex items-center gap-3 p-4">
            <div className="w-10 h-10 flex items-center justify-center border border-border font-display font-bold uppercase">{(s.name || s.username || "?")[0]}</div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold uppercase tracking-tight truncate">{s.name || s.username}</div>
              <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground">@{s.username} · staff</div>
            </div>
            <button data-testid={`delete-staff-${s.id}`} onClick={() => remove(s)} className="p-2 border border-border hover:border-destructive hover:text-destructive transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
